import { Injectable } from '@angular/core';
import {
  CheckboxQuestion,
  EntityQuestion,
  HiddenQuestion,
  QuestionBase,
  QuestionGroup,
  TextboxQuestion,
  DropdownQuestion,
  TagsQuestion
} from 'core/components/forms';
import {
  ClientsService,
  LocalesService,
  ProfileService,
  CoursesService,
  CoreQuestionsFactoryService
} from 'core/services';
import { Group } from 'core/models/group';

@Injectable()
export class StudentsQuestionsService {
  userRole;

  constructor(
    private factory: CoreQuestionsFactoryService,
    private clientsService: ClientsService,
    private coursesService: CoursesService,
    private localesService: LocalesService,
    private profileService: ProfileService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getQuestions(student: any, client?: any, group?: any) {
    client = student ? { id: student.clientId, name: student.clientName } : client;
    const clientQuestion = this.factory.getClientQuestion({
      client,
      title: 'actions.selectClient',
      class: 'col-1',
      validators: { required: true },
      editable: !client,
      order: 1
    });
    const selfRegisteredQuestion = student
      ? new CheckboxQuestion({
          key: 'selfRegistered',
          label: 'students.info.selfRegistered',
          disabled: true,
          value: student.selfRegistered,
          order: 12
        })
      : new HiddenQuestion({
          key: 'selfRegistered',
          value: false,
          order: 100
        });

    const questions: QuestionBase<any>[] = [
      clientQuestion,
      new TextboxQuestion({
        key: 'email',
        label: 'nouns.email',
        type: 'email',
        disabled: student ? true : false,
        editable: student ? false : true,
        value: student && student.email ? student.email : null,
        validators: { required: true, mail: true },
        order: 2
      }),
      new TextboxQuestion({
        key: 'name',
        label: 'students.header.name',
        class: 'col-2',
        value: student && student.name ? student.name : null,
        validators: { required: group ? group.hasBonus : false },
        order: 3
      }),
      new TextboxQuestion({
        key: 'surname',
        label: 'nouns.surname',
        class: 'col-2',
        value: student && student.surname ? student.surname : null,
        validators: { required: group ? group.hasBonus : false },
        order: 4
      }),
      new TextboxQuestion({
        key: 'pid',
        label: 'nouns.pid',
        class: 'col-2-offset',
        value: student && student.pid ? student.pid : null,
        validators: { required: group ? group.hasBonus : false },
        order: 5
      }),
      new TextboxQuestion({
        key: 'phone',
        label: 'nouns.phone',
        class: 'col-2-offset',
        value: student && student.phone ? student.phone : null,
        order: 6
      }),
      new DropdownQuestion({
        key: 'lang',
        label: 'nouns.language',
        class: 'col-2-offset',
        value: student && student.lang ? student.lang : null,
        placeholder: 'actions.selectLanguage',
        options: this.localesService.getExtendedOptions(),
        order: 7
      }),
      new TextboxQuestion({
        key: 'alias',
        label: 'nouns.alias',
        class: 'col-2-offset',
        value: student && student.alias ? student.alias : null,
        order: 9
      }),
      new TextboxQuestion({
        key: 'comments',
        label: 'nouns.comments',
        value: student && student.comments ? student.comments : null,
        order: 10
      }),
      new TagsQuestion({
        key: 'tags',
        label: 'nouns.tags',
        value: student && student.tags ? student.tags : [],
        order: 11
      }),
      selfRegisteredQuestion
    ];

    return questions.sort((a, b) => a.order - b.order);
  }

  getCsvQuestions(students: any[], group: Group) {
    // Update langs for selected course in localesService when group defined
    const courseOptions = this.coursesService.getOptions();
    const selectedCourseInfo = courseOptions.find((courseOption) => courseOption.key === group.customizationId);
    this.localesService.updateArrayCodeIso(selectedCourseInfo.data.languages);

    return students.map(
      (student, index) =>
        new QuestionGroup({
          key: index + 1,
          class: 'row',
          order: index,
          error: student.error,
          hideError: student.hideError,
          childQuestions: this.getLineCsvQuestions(student, group),
          hide: false
        })
    );
  }

  getLineCsvQuestions(student: any, group: Group) {
    const questions: QuestionBase<any>[] = [
      new TextboxQuestion({
        key: 'userEmail',
        type: 'email',
        class: 'col s4 gap-right',
        value: student && student.userEmail ? student.userEmail : null,
        validators: { required: true, mail: true },
        disabled: student.disabled,
        editable: false
      }),
      new TextboxQuestion({
        key: 'name',
        class: 'col s2 gap-right',
        value: student && student.name ? student.name : null,
        disabled: student.disabled,
        editable: false
      }),
      new TextboxQuestion({
        key: 'surname',
        class: 'col s4 gap-right',
        value: student && student.surname ? student.surname : null,
        disabled: student.disabled,
        editable: false
      }),
      new TextboxQuestion({
        key: 'alias',
        class: 'col s2 gap-right',
        value: student && student.alias ? student.alias : null,
        disabled: student.disabled,
        editable: false
      })
    ];

    if (group.hasBonus) {
      questions.push(
        new TextboxQuestion({
          key: 'pid',
          class: 'col s2 gap gap-right',
          value: student && student.pid ? student.pid : null,
          validators: { required: group.hasBonus },
          disabled: student.disabled
        })
      );
    }

    return questions;
  }

  getImportStudentsQuestions(client?: any) {
    const clientQuestion =
      this.userRole.scope === 'client'
        ? new HiddenQuestion({
            key: 'clientId',
            value: client.id,
            validators: { required: true },
            order: 100
          })
        : new EntityQuestion({
            key: 'clientId',
            label: 'nouns.client',
            value: client ? client.id : null,
            entity: client,
            editable: !client,
            validators: { required: true },
            dataProvider: this.clientsService,
            entityOptions: { filters: { 'scorm.enabled': 'false' } },
            order: 1
          });
    const questions: QuestionBase<any>[] = [clientQuestion];
    return questions;
  }
}
